import Axios from '@/utils/axios';
import { HandoverListRequest, HandoverListResponse } from '@/types/common';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// --- Per-part handover checklist (backend source of truth) ---
export type ChecklistCategory = 'EQUIPMENT' | 'CONDITION';

export interface ChecklistCatalogItem {
  code: string;
  label: string;
  category: ChecklistCategory;
  allowAbsent: boolean;
}

export interface ChecklistCatalogGroup {
  code: string;
  title: string;
  items: ChecklistCatalogItem[];
}

export interface ChecklistCatalog {
  version: number;
  groups: ChecklistCatalogGroup[];
}

/** One captured checklist result sent in the create payload. */
export interface HandoverChecklistItemInput {
  code: string;
  present: boolean;
  /** Required only when present. */
  photoUrl?: string;
}

export interface CreateHandoverRequest {
  vehicleID: string;
  driverID?: number | null;
  driverName?: string | null;
  driverCode?: string | null;
  clientID: number;
  clientName: string;
  hubID: number;
  hubName: string;
  hubCityID: number;
  hubCityName: string;
  handoverDate: string;
  bastLink?: string;
  notes?: string;
  vehicleHandoverImageURL?: string;
  odometer?: number;
  depositMitraToDash?: number;
  unitEquipment?: string[];
  returnUnitEquipment?: string[];
  // Structured per-part checklist. When set, the server derives unitEquipment +
  // photos from it; the legacy flat fields above become optional.
  checklistItems?: HandoverChecklistItemInput[];
  sellingPrice?: number;
  createdBy: string;
}

export interface BookingListRequest {
  page?: string;
  size?: string;
  sort?: string;
  order?: string;
  search?: string;
  statuses?: string;
  oemIDs?: string;
  modelIDs?: string;
  hubIDs?: string;
  businessUnits?: string;
  startDate?: string;
  endDate?: string;
  handoverStartDate?: string;
  handoverEndDate?: string;
  returnStartDate?: string;
  returnEndDate?: string;
}

export interface BookingListItem {
  ID?: string;
  id?: string | number;
  vehicleID?: string;
  platNomor?: string;
  oemName?: string;
  modelName?: string;
  driverID?: number | string;
  mitraID?: number | string;
  driverCode?: string;
  mitraCode?: string;
  driverName?: string;
  mitraName?: string;
  firstName?: string;
  lastName?: string;
  nik?: string;
  noKTP?: string;
  driverNIK?: string;
  driverPhoneNumber?: string;
  phone?: string;
  noWhatsapp?: string;
  whatsappNumber?: string;
  driverStatus?: string;
  driverEmail?: string;
  status?: string;
  bookingStatus?: string;
  cityName?: string;
  domicileCity?: string;
  kotaDomisili?: string;
  hubID?: number | string;
  hubName?: string;
  hubCityID?: number | string;
  hubCityName?: string;
  hubLatitude?: string;
  hubLongitude?: string;
  businessUnit?: string;
  bookingDate?: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
}

export interface BookingListResponse {
  data: BookingListItem[];
  pagination?: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export interface CreateBookingRequest {
  vehicleID: string;
  driverID?: number | null;
  driverName?: string | null;
  driverCode?: string | null;
  hubID: number;
  hubName: string;
  hubCityID: number;
  hubCityName: string;
  hubLatitude?: string;
  hubLongitude?: string;
  businessUnit: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
  createdBy: string;
}

export interface UpdateBookingRequest {
  vehicleID?: string;
  driverID?: number | null;
  driverName?: string | null;
  driverCode?: string | null;
  hubID?: number;
  hubName?: string;
  hubCityID?: number;
  hubCityName?: string;
  hubLatitude?: string;
  hubLongitude?: string;
  businessUnit?: string;
  bookingDate?: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
  updatedBy: string;
}

export interface UpdateHandoverRequest {
  vehicleID?: string;
  driverID?: number | null;
  driverName?: string | null;
  driverCode?: string | null;
  clientID?: number;
  clientName?: string;
  hubID?: number;
  hubName?: string;
  hubCityID?: number;
  hubCityName?: string;
  handoverDate?: string;
  bastLink?: string;
  notes?: string;
  vehicleHandoverImageURL?: string;
  odometer?: number;
  depositMitraToDash?: number;
  unitEquipment?: string[];
  returnUnitEquipment?: string[];
  checklistItems?: HandoverChecklistItemInput[];
  sellingPrice?: number;
  updatedBy: string;
}

export const getListHandover = async (
  params?: HandoverListRequest
): Promise<HandoverListResponse> => {
  try {
    const response = await Axios.get('/v1/handovers', { params });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface VehicleHandoverHistory {
  ID: string;
  vehicleID: string;
  platNomor: string;
  oemName: string;
  modelName: string;
  driverID: number | null;
  driverName: string | null;
  driverCode: string | null;
  clientID: number | null;
  clientName: string | null;
  hubID: number | null;
  hubName: string | null;
  vehicleHubName: string | null;
  vehicleBusinessUnit: string | null;
  handoverDate: string;
  returnDate: string | null;
  returnDuration: number;
  returnReason: string | null;
  returnReasonOther: string | null;
  status: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Returned-handover history for a single vehicle, newest return first. */
export const getVehicleHandoverHistory = async (
  vehicleID: string
): Promise<VehicleHandoverHistory[]> => {
  try {
    const response = await Axios.get(`/v1/handovers/vehicles/${vehicleID}`);
    return response.data?.data || response.data || [];
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getListBooking = async (
  params?: BookingListRequest
): Promise<BookingListResponse> => {
  try {
    const response = await Axios.get('/v1/handovers/bookings', { params });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const createBooking = async (
  data: CreateBookingRequest
): Promise<any> => {
  try {
    const response = await Axios.post('/v1/handovers/bookings', data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const updateBooking = async (
  id: string,
  data: UpdateBookingRequest
): Promise<any> => {
  try {
    const response = await Axios.patch(`/v1/handovers/bookings/${id}`, data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const cancelBooking = async (
  id: string,
  data: { updatedBy: string }
): Promise<any> => {
  try {
    const response = await Axios.delete(`/v1/handovers/bookings/${id}`, {
      data,
    });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const createHandover = async (
  data: CreateHandoverRequest
): Promise<any> => {
  try {
    const response = await Axios.post('/v1/handovers', data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

/**
 * Canonical per-part checklist taxonomy (backend source of truth, shared with
 * Fleet Mobile). Callers should fall back to a local default if this fails.
 */
export const getHandoverChecklistCatalog =
  async (): Promise<ChecklistCatalog> => {
    const response = await Axios.get('/v1/handovers/checklist-catalog');
    return response.data?.data as ChecklistCatalog;
  };

export const updateHandover = async (
  id: string,
  data: UpdateHandoverRequest
): Promise<any> => {
  try {
    const response = await Axios.patch(`/v1/handovers/${id}`, data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

/**
 * Ops schedule override. Send only what moves — but at least one field, or the
 * server 422s rather than reporting a save that changed nothing.
 *
 * Which fields are legal depends on the rung: the pickup slot only while
 * WAITING_HANDOVER, the return date only while WAITING_RETURN. Anything else is
 * a 409 naming the rung. `slotEndTime` is normally left out: the server resolves
 * it from the hub's configured window for `slotStartTime`, so the pair cannot
 * describe a window the hub does not open.
 */
export interface UpdateHandoverScheduleRequest {
  /** `YYYY-MM-DD` — a calendar day at a hub, never an instant. */
  scheduledHandoverDate?: string;
  /** `HH:mm`. */
  slotStartTime?: string;
  slotEndTime?: string;
  scheduledReturnDate?: string;
  updatedBy: string;
}

export interface UpdateHandoverScheduleResponse {
  handoverID: string;
  /** Null for a client assignment — no rental to mirror the dates onto. */
  rentalID: string | null;
  scheduledHandoverDate: string | null;
  slotStartTime: string | null;
  slotEndTime: string | null;
  scheduledReturnDate: string | null;
}

export const updateHandoverSchedule = async (
  id: string,
  data: UpdateHandoverScheduleRequest
): Promise<UpdateHandoverScheduleResponse> => {
  try {
    const response = await Axios.patch(`/v1/handovers/${id}/schedule`, data);
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface ReturnHandoverRequest {
  returnDate: string;
  returnOdometer?: number;
  // Per-part checklist captured at return (source of truth).
  checklistItems?: HandoverChecklistItemInput[];
  returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
  returnReason:
    | 'ACCIDENT'
    | 'CONTRACT_END'
    | 'MITRA_RESIGN'
    | 'CLIENT_REQUEST'
    | 'DUTY_COMPLETED'
    | 'REPOSSESSION'
    | 'VEHICLE_SWAP'
    | 'OTHER';
  returnReasonOther?: string;
  updatedBy: string;
  vehicleReturnImageURL?: string;
  vehicleAndMitraReturnImageURL?: string;
  returnBastLink?: string;
  returnNotes?: string;
  // Dash Rent: mengembalikan unit yang masih terikat kontrak juga mengakhiri
  // kontraknya dan bisa mencetak tagihan tunggakan. Tanpa flag ini backend
  // menolak 409 — sengaja, supaya kontrak tidak bisa ditutup tanpa ops
  // melihat dampaknya lebih dulu.
  confirmRentalTermination?: boolean;
}

// Apa yang akan terjadi pada kontrak kalau unit ini dikembalikan sekarang.
// Angkanya datang dari backend, bukan dihitung ulang di sini: rumusnya satu
// dan tinggal di sisi yang juga menuliskannya.
export interface RentalTerminationImpact {
  rentalID: string;
  rentalCode: string;
  driverID: number;
  driverName: string | null;
  terminationReason: string;
  // Hari yang sudah jatuh tempo: utang sungguhan, jadi tagihan outstanding
  // yang tetap tertagih sesudah kontraknya tutup.
  arrearsOwed: number;
  arrearsDays: number;
  // Hari yang belum jatuh tempo: dibatalkan, tidak pernah jadi utang.
  chargesVoided: number;
  prepaidToRefund: number;
  depositPaidTotal: number;
  outstandingAlreadyExists: boolean;
}

// null artinya unitnya tidak terikat kontrak apa pun — return biasa.
export const getReturnImpact = async (
  id: string,
  returnReason: string,
  returnDate: string
): Promise<RentalTerminationImpact | null> => {
  try {
    const response = await Axios.get(`/v1/handovers/${id}/return-impact`, {
      params: { returnReason, returnDate },
    });
    const data = response.data?.data || response.data;
    return data?.rentalTerminationImpact ?? null;
  } catch (error: any) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getHandoverById = async (id: string): Promise<any> => {
  try {
    const response = await Axios.get(`/v1/handovers/${id}`);
    return response.data?.data || response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const returnHandover = async (
  id: string,
  data: ReturnHandoverRequest
): Promise<any> => {
  try {
    const response = await Axios.patch(`/v1/handovers/${id}/return`, data);
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface CheckActiveHandoverResponse {
  hasActiveHandover: boolean;
  message?: string;
}

export const checkActiveHandover = async (
  driverId: string
): Promise<CheckActiveHandoverResponse> => {
  try {
    const response = await Axios.get(
      `/v1/handovers/drivers/${driverId}/check-active-handover`
    );

    const hasActiveHandover = response.data?.data?.hasActiveHandover || false;

    return {
      hasActiveHandover,
      message: hasActiveHandover
        ? 'Driver has an active handover'
        : 'Driver is available',
    };
  } catch (error: any) {
    // If 404 or error, assume no active handover
    return {
      hasActiveHandover: false,
      message: 'Driver is available',
    };
  }
};

export const downloadActiveHandoversCsv = async (
  params?: Record<string, any>
): Promise<Blob> => {
  try {
    const response = await Axios.get('/v1/handovers/active/download/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const downloadReturnedHandoversCsv = async (
  params?: Record<string, any>
): Promise<Blob> => {
  try {
    const response = await Axios.get('/v1/handovers/returned/download/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

/**
 * Per-tab badge counts for the handover list.
 *
 * Its own endpoint because the counts used to be whatever the mounted tab
 * reported, so every badge but the open one read zero until it was clicked.
 *
 * Takes the list's non-tab filters, minus the date ranges: each tab means a
 * different column by "date", so one range cannot narrow every bucket.
 */
export interface HandoverStats {
  waitingHandover: number;
  active: number;
  waitingReturn: number;
  returned: number;
}

export const getHandoverStats = async (
  params?: Record<string, any>
): Promise<HandoverStats> => {
  try {
    const response = await Axios.get('/v1/handovers/stats', { params });
    return response.data?.data ?? response.data;
  } catch (error: any) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};
